import { useMemo, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { CONDITION_COLORS, DEMO_LISTINGS, demoListingTier, type ConditionRating, type DemoListing } from '@redevicer/shared'
import { conditionLabel } from '../utils/conditionLabel'
import ExampleListingDetailModal from './ExampleListingDetailModal'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'

export interface ExamplesShowcaseProps {
  onOpenCatalog: () => void
}

function IconExamples() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="7" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
      <rect x="13" y="5" width="7" height="9" rx="2" stroke="currentColor" strokeWidth="2" opacity="0.75" />
      <path d="M7.5 17.5h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

export default function ExamplesShowcase({ onOpenCatalog }: ExamplesShowcaseProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [conditionFilter, setConditionFilter] = useState('')
  const [tierFilter, setTierFilter] = useState<'all' | 'budget' | 'mid' | 'premium'>('all')
  const [selected, setSelected] = useState<DemoListing | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return DEMO_LISTINGS.filter((item) => {
      const tier = demoListingTier(item.price)
      const tierOk =
        tierFilter === 'all' ||
        (tierFilter === 'budget' && tier === 'budget') ||
        (tierFilter === 'mid' && tier === 'mid') ||
        (tierFilter === 'premium' && tier === 'premium')
      const condOk = !conditionFilter || item.condition === conditionFilter
      if (!tierOk || !condOk) return false
      if (!q) return true
      const hay = `${item.manufacturer} ${item.model} ${item.title} ${item.seller} ${item.location}`.toLowerCase()
      return hay.includes(q)
    }).sort((a, b) => a.price - b.price)
  }, [query, conditionFilter, tierFilter])

  const grouped = useMemo(() => {
    const budget = filtered.filter((l) => demoListingTier(l.price) === 'budget')
    const mid = filtered.filter((l) => demoListingTier(l.price) === 'mid')
    const premium = filtered.filter((l) => demoListingTier(l.price) === 'premium')
    return { budget, mid, premium }
  }, [filtered])

  function renderCard(item: DemoListing, idx: number) {
    return (
      <button
        key={item.id}
        type="button"
        className="listing-card listing-card--example examples-card examples-card--clickable"
        style={{ '--example-i': idx } as CSSProperties}
        onClick={() => setSelected(item)}
        aria-label={`${t('examples.viewDemo')}: ${item.manufacturer} ${item.model}`}
      >
        <div className="listing-card-cover examples-card__cover">
          <ListingNoPhotoPlaceholder labelKey="examples.photoLabel" />
          <span className="example-ribbon">{t('examples.ribbon')}</span>
        </div>
        <div className="listing-card-body examples-card__body">
          <p className="example-seller">
            <span className="example-seller-name">{item.seller}</span>
            <span className="example-location">{item.location}</span>
          </p>
          <h3>{item.manufacturer}</h3>
          <p className="model-name">{item.model}</p>
          <p className="title">{item.title}</p>
          <p className="price examples-card__price">€{item.price.toFixed(2)}</p>
          <div className="listing-card-badges">
            <span
              className="condition-badge"
              style={{ backgroundColor: CONDITION_COLORS[item.condition as ConditionRating] }}
            >
              {conditionLabel(item.condition, t)}
            </span>
            {item.battery != null && item.battery > 0 && (
              <span className="battery">
                <span className="battery-mark" aria-hidden="true">⏻</span>
                {item.battery}%
              </span>
            )}
          </div>
          <span className="example-cta-hint">{t('examples.viewDemo')} →</span>
        </div>
      </button>
    )
  }

  return (
    <>
      <section className="examples-showcase examples-showcase--modern">
        <header className="examples-hero examples-hero--app">
          <div className="examples-hero__glow" aria-hidden="true" />
          <div className="examples-hero__icon" aria-hidden="true">
            <IconExamples />
          </div>
          <h2>{t('examples.title')}</h2>
          <p className="examples-lead">{t('examples.lead')}</p>
          <p className="examples-disclaimer">{t('examples.disclaimer')}</p>
          <div className="examples-hero-actions">
            <button type="button" className="examples-primary-btn" onClick={onOpenCatalog}>
              {t('examples.openCatalog')}
            </button>
          </div>
        </header>

        <div className="examples-filters-panel">
          <div className="filters examples-filters examples-filters--app">
            <input
              type="search"
              placeholder={t('examples.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label={t('examples.searchPlaceholder')}
            />
            <select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)}>
              <option value="">{t('catalog.allConditions')}</option>
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="damaged">Damaged</option>
            </select>
            <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value as typeof tierFilter)}>
              <option value="all">{t('examples.tierAll')}</option>
              <option value="budget">{t('examples.tierBudget')}</option>
              <option value="mid">{t('examples.tierMid')}</option>
              <option value="premium">{t('examples.tierPremium')}</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="examples-empty examples-empty--bloom">
            <div className="examples-empty__glow" aria-hidden="true" />
            <p className="examples-empty__title">{t('examples.empty')}</p>
            <button
              type="button"
              className="examples-empty__clear"
              onClick={() => {
                setQuery('')
                setConditionFilter('')
                setTierFilter('all')
              }}
            >
              {t('catalog.clearAllFilters')}
            </button>
          </div>
        ) : (
          <div className="examples-groups">
            {(tierFilter === 'all' ? ['budget', 'mid', 'premium'] : [tierFilter]).map((key, groupIdx) => {
              const list =
                key === 'budget'
                  ? grouped.budget
                  : key === 'mid'
                    ? grouped.mid
                    : grouped.premium
              if (!list.length) return null
              return (
                <div key={key} className="examples-group" style={{ '--group-i': groupIdx } as CSSProperties}>
                  <h3 className="examples-group-title">
                    {key === 'budget' && t('examples.groupBudget')}
                    {key === 'mid' && t('examples.groupMid')}
                    {key === 'premium' && t('examples.groupPremium')}
                    <span className="examples-count">({list.length})</span>
                  </h3>
                  <div className="listings-grid examples-grid">{list.map((item, idx) => renderCard(item, idx))}</div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <ExampleListingDetailModal
        listing={selected}
        onClose={() => setSelected(null)}
        onOpenCatalog={() => {
          setSelected(null)
          onOpenCatalog()
        }}
      />
    </>
  )
}
